/**
 * The sentinel follows the pointer.
 *
 * A flat plate cannot turn its head, so "looking at you" is a perspective tilt toward the pointer
 * plus a small translation the same way, with the halo behind it moving the other way for depth.
 * `tiltFor` is the whole rule and is pure; the hook writes its four numbers as CSS variables on the
 * stage and lets the stylesheet decide what to do with them.
 *
 * Signs, so nobody has to re-derive them: CSS rotateY(+) turns the face toward the viewer's RIGHT,
 * rotateX(+) tips the face UP. So ry follows the pointer's x and rx follows the NEGATIVE of its y.
 */
import { useEffect, type RefObject } from 'react';

export interface Tilt {
  rx: number;
  ry: number;
  tx: number;
  ty: number;
}

const MAX_RY = 14;
const MAX_RX = 8;
const MAX_TX = 18;
const MAX_TY = 10;

const clamp = (n: number) => Math.max(-1, Math.min(1, n));

export function tiltFor(
  px: number,
  py: number,
  rect: { left: number; top: number; width: number; height: number },
): Tilt {
  if (rect.width <= 0 || rect.height <= 0) return { rx: 0, ry: 0, tx: 0, ty: 0 };
  const nx = clamp((px - (rect.left + rect.width / 2)) / (rect.width / 2));
  const ny = clamp((py - (rect.top + rect.height / 2)) / (rect.height / 2));
  // `-ny || 0` turns the -0 a centred pointer would produce back into 0.
  return { ry: nx * MAX_RY, rx: (-ny || 0) * MAX_RX, tx: nx * MAX_TX, ty: ny * MAX_TY };
}

/** Writes `--rx --ry --tx --ty` on `ref`'s element from the pointer's position anywhere on the page. */
export function usePointerTilt(ref: RefObject<HTMLElement | null>, enabled = true): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    let last: Tilt | null = null;
    const paint = () => {
      frame = 0;
      if (!last) return;
      el.style.setProperty('--rx', `${last.rx.toFixed(2)}deg`);
      el.style.setProperty('--ry', `${last.ry.toFixed(2)}deg`);
      el.style.setProperty('--tx', `${last.tx.toFixed(1)}px`);
      el.style.setProperty('--ty', `${last.ty.toFixed(1)}px`);
    };
    const onMove = (e: PointerEvent) => {
      last = tiltFor(e.clientX, e.clientY, el.getBoundingClientRect());
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const onLeave = () => {
      last = { rx: 0, ry: 0, tx: 0, ty: 0 };
      if (!frame) frame = requestAnimationFrame(paint);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, enabled]);
}
